import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBrand } from '@/config/brands';
import { type FraudCategory } from '@/config/fraudCategories';
import { type NationalBenchmark, type HospitalCodePricing } from '@/hooks/useFraudCategoryPricing';
import { markupBadgeColor } from '@/lib/grades';
import { loadCodeDetail } from '@/lib/codeDetailStore';

// Patriotic palette
const NAVY = '#002868';
const CREAM = '#FAF9F6';

interface CategoryCodeTableProps {
  category: FraudCategory;
  benchmarks: NationalBenchmark[];
  hospitalPricingMap: Map<string, HospitalCodePricing[]>;
  totalHospitals: number;
}

const fmt = (v: number | null) => {
  if (v == null || isNaN(v)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v);
};

const fmtPct = (v: number | null) => {
  if (v == null || isNaN(v)) return '—';
  const sign = v >= 0 ? '+' : '';
  if (Math.abs(v) >= 1000) return `${sign}${(v / 1000).toFixed(1)}k%`;
  return `${sign}${v.toFixed(1)}%`;
};

const median = (xs: number[]): number | null => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

const pctDiff = (value: number | null, baseline: number | null): number | null =>
  value != null && baseline != null && baseline > 0 ? ((value - baseline) / baseline) * 100 : null;

// CDN code-detail files are keyed by family slug + the stored code form
// (DRGs are stored as MS-###).
const detailKey = (codeType: string, code: string): [string, string] =>
  codeType === 'DRG' ? ['drg', `MS-${code}`] : [codeType.toLowerCase(), code];

interface ConsumerName { name: string; summary: string }

export const CategoryCodeTable = ({
  category,
  benchmarks,
  hospitalPricingMap,
}: CategoryCodeTableProps) => {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const { t } = useTranslation(['homepage']);

  // Consumer-friendly names from the CDN code-detail files — derived from
  // hospitals' own published chargemaster wording, never the AMA descriptor.
  const [names, setNames] = useState<Record<string, ConsumerName>>({});
  useEffect(() => {
    let cancelled = false;
    Promise.all(
      category.codes.map(async (c) => {
        const [family, code] = detailKey(c.codeType, c.code);
        try {
          const d = await loadCodeDetail(family, code);
          return d?.consumerName
            ? ([`${c.codeType}:${c.code}`, { name: d.consumerName, summary: d.llmSummary || '' }] as const)
            : null;
        } catch {
          return null;
        }
      })
    ).then((pairs) => {
      if (cancelled) return;
      const next: Record<string, ConsumerName> = {};
      for (const p of pairs) if (p) next[p[0]] = p[1];
      setNames(next);
    });
    return () => { cancelled = true; };
  }, [category]);

  const rows = useMemo(() => {
    // Per code: one value per hospital (that hospital's own median across its
    // settings) → state median + state avg across hospitals, plus the most
    // common hospital wording for the tooltip.
    const perCode = new Map<string, { byHospital: Map<string, number[]>; wording: Map<string, number> }>();
    for (const [hospitalId, pricing] of hospitalPricingMap) {
      for (const hp of pricing) {
        if (!Number.isFinite(hp.hospitalGrossCharge) || hp.hospitalGrossCharge <= 0) continue;
        const entry = perCode.get(hp.procedureCode) || { byHospital: new Map(), wording: new Map() };
        const vals = entry.byHospital.get(hospitalId) || [];
        vals.push(hp.hospitalGrossCharge);
        entry.byHospital.set(hospitalId, vals);
        const w = (hp.procedureDescription || '').trim();
        if (w) entry.wording.set(w, (entry.wording.get(w) || 0) + 1);
        perCode.set(hp.procedureCode, entry);
      }
    }

    const built = category.codes.map((catCode) => {
      const benchmark = benchmarks.find(
        (b) => b.code === catCode.code && b.codeType === catCode.codeType
      );
      const agg = perCode.get(catCode.code);
      const hospitalValues = agg
        ? [...agg.byHospital.values()].map((v) => median(v)).filter((v): v is number => v != null)
        : [];
      const stateMedian = median(hospitalValues);
      const stateAvg = hospitalValues.length
        ? hospitalValues.reduce((a, b) => a + b, 0) / hospitalValues.length
        : null;
      const nationalMedian = benchmark?.nationalMedian ?? null;
      const nationalAvg = benchmark?.nationalAvg ?? null;
      const hospitalWording = agg
        ? ([...agg.wording.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null)
        : null;
      const consumer = names[`${catCode.codeType}:${catCode.code}`];
      // CPT descriptors are AMA-licensed and never shown — a code with no
      // consumer name yet gets the neutral 'CPT <code>'. HCPCS / DRG / REV
      // descriptors are public and stay as the fallback.
      const displayName = consumer?.name
        ?? (catCode.codeType === 'CPT' ? `CPT ${catCode.code}` : catCode.description);

      return {
        code: catCode.code,
        codeType: catCode.codeType,
        displayName,
        summary: consumer?.summary ?? null,
        hospitalWording,
        nationalMedian,
        nationalAvg,
        nationalHospitals: benchmark?.totalHospitals ?? 0,
        stateMedian,
        stateAvg,
        medianMarkup: pctDiff(stateMedian, nationalMedian),
        avgMarkup: pctDiff(stateAvg, nationalAvg),
        hospCount: hospitalValues.length,
      };
    });

    // Worst median markup first, nulls last
    built.sort((a, b) => {
      if (a.medianMarkup == null && b.medianMarkup == null) return 0;
      if (a.medianMarkup == null) return 1;
      if (b.medianMarkup == null) return -1;
      return b.medianMarkup - a.medianMarkup;
    });
    return built;
  }, [category, benchmarks, hospitalPricingMap, names]);

  const muted = isAhp ? 'rgba(27,27,27,0.6)' : undefined;
  const faint = isAhp ? 'rgba(27,27,27,0.45)' : undefined;
  const aiNamesNote = t('homepage:homeBeta.procedureDrilldown.aiNamesNote', {
    defaultValue: "Procedure names are AI-generated consumer-friendly names derived from hospitals' own published chargemaster descriptions — not official code descriptors. CPT® is a registered trademark of the AMA; not affiliated. Hover a row for the hospital's own wording.",
  });

  const headers: Array<{ label: string; sub?: string; align: 'left' | 'right' | 'center' }> = [
    { label: 'Code', align: 'left' },
    { label: 'Type', align: 'left' },
    { label: 'Description', align: 'left' },
    { label: 'National Median', sub: 'avg beneath', align: 'right' },
    { label: 'State Median', sub: 'avg beneath', align: 'right' },
    { label: 'Median Markup', sub: 'state vs national', align: 'right' },
    { label: 'Hospitals', align: 'center' },
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr
            style={isAhp ? {
              background: `linear-gradient(135deg, ${NAVY} 0%, #001a4a 100%)`,
            } : undefined}
            className={!isAhp ? 'border-b bg-muted/50' : ''}
          >
            {headers.map((h) => (
              <th
                key={h.label}
                className={`p-2.5 font-semibold text-xs tracking-wide uppercase text-${h.align}`}
                style={isAhp ? { color: CREAM } : undefined}
              >
                {h.label}
                {h.sub && (
                  <span className="block text-[9px] font-normal normal-case tracking-normal opacity-70">{h.sub}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={headers.length}
                className="text-center py-8"
                style={{ color: isAhp ? 'rgba(27,27,27,0.35)' : undefined }}
              >
                No pricing data available for these codes
              </td>
            </tr>
          ) : (
            rows.map((row, idx) => (
              <tr
                key={`${row.codeType}-${row.code}`}
                style={isAhp ? {
                  borderBottom: '1px solid rgba(0,0,0,0.06)',
                  backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(0,40,104,0.02)',
                } : undefined}
                className={!isAhp ? 'border-b hover:bg-muted/30' : ''}
              >
                <td
                  className="p-2.5 font-mono text-xs"
                  style={isAhp ? { color: NAVY, fontWeight: 600 } : undefined}
                >
                  {row.code}
                </td>
                <td className="p-2.5 text-xs">
                  <span
                    className="px-1.5 py-0.5 rounded text-xs font-semibold"
                    style={isAhp ? {
                      backgroundColor: `${NAVY}10`,
                      color: NAVY,
                      border: `1px solid ${NAVY}20`,
                    } : {
                      backgroundColor: `${category.color}15`,
                      color: category.color,
                    }}
                  >
                    {row.codeType}
                  </span>
                </td>
                <td
                  className="p-2.5 text-xs max-w-xs truncate"
                  title={row.hospitalWording
                    ? `Hospital's own wording: ${row.hospitalWording}`
                    : (row.summary || row.displayName)}
                  style={isAhp ? { color: 'rgba(27,27,27,0.7)' } : undefined}
                >
                  {row.displayName}
                </td>
                <td className="p-2.5 text-right font-mono text-xs" style={{ color: muted }}>
                  <div>{fmt(row.nationalMedian)}</div>
                  <div className="text-[10px]" style={{ color: faint }}>avg {fmt(row.nationalAvg)}</div>
                </td>
                <td className="p-2.5 text-right font-mono text-xs" style={{ color: muted }}>
                  <div>{fmt(row.stateMedian)}</div>
                  <div className="text-[10px]" style={{ color: faint }}>avg {fmt(row.stateAvg)}</div>
                </td>
                <td className="p-2.5 text-right font-mono text-xs font-bold">
                  {row.medianMarkup != null ? (
                    <span style={{ color: markupBadgeColor(row.medianMarkup) }}>
                      {fmtPct(row.medianMarkup)}
                    </span>
                  ) : (
                    <span style={{ color: isAhp ? 'rgba(27,27,27,0.25)' : undefined }} className={!isAhp ? 'text-muted-foreground' : ''}>—</span>
                  )}
                  <div className="text-[10px] font-normal" style={{ color: faint }}>avg {fmtPct(row.avgMarkup)}</div>
                </td>
                <td
                  className="p-2.5 text-center font-mono text-xs"
                  style={isAhp ? {
                    color: row.hospCount > 0 ? NAVY : 'rgba(27,27,27,0.25)',
                    fontWeight: row.hospCount > 0 ? 600 : 400,
                  } : undefined}
                >
                  {row.hospCount > 0 ? row.hospCount : '—'}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <div
        className="px-3 py-2.5 text-[11px] leading-relaxed space-y-1"
        style={{ color: isAhp ? 'rgba(27,27,27,0.55)' : undefined }}
      >
        <p>
          Median = the middle hospital's published gross charge (half charge less, half more); avg = the plain mean.
          Markup compares this state's median to the national median for the same code — the same basis as the hospital A–F grades.
        </p>
        <p>{aiNamesNote}</p>
      </div>
    </div>
  );
};
